import { http } from '@noor/api';
import { env } from '@noor/config';
import * as z from 'zod';
import {
  storefrontDayScheduleSchema,
  storefrontItemSchema,
  type StorefrontEntity,
  type StorefrontItem,
} from '../model/schema';

const paths: Record<StorefrontEntity, string> = {
  restaurants: 'restaurants',
  categories: 'product-categories',
  products: 'products',
  promotions: 'promotions',
};
const detailKeys: Record<StorefrontEntity, string> = {
  restaurants: 'restaurant',
  categories: 'category',
  products: 'product',
  promotions: 'promotion',
};
const base = { baseURL: env.VITE_EATS_API_URL };
const envelope = z.object({
  data: z.record(z.string(), z.unknown()).optional(),
  list: z.array(storefrontItemSchema).optional(),
  meta: z
    .object({
      total: z.coerce.number().optional(),
      page_count: z.coerce.number().optional(),
    })
    .optional(),
});
const detailEnvelope = z.object({
  data: z.record(z.string(), z.unknown()).optional(),
  list: z.array(z.unknown()).optional(),
  meta: z.record(z.string(), z.unknown()).optional(),
});

export interface CatalogSearch {
  page: number;
  pageSize: number;
  merchant_id: string;
  restaurant_id: string;
  name: string;
}

export const catalogSearchSchema = z.object({
  page: z.coerce.number().int().min(1).catch(1),
  pageSize: z.coerce.number().int().min(1).max(100).catch(20),
  merchant_id: z.string().catch(''),
  restaurant_id: z.string().catch(''),
  name: z.string().catch(''),
});

export const catalogApi = {
  async list(entity: StorefrontEntity, search: CatalogSearch, signal?: AbortSignal) {
    const query: Record<string, string> = {
      'pagination.limit': String(search.pageSize),
      'pagination.offset': String((search.page - 1) * search.pageSize),
    };
    if (search.merchant_id.trim()) query.merchant_id = search.merchant_id.trim();
    if (entity !== 'restaurants' && search.restaurant_id.trim())
      query.restaurant_id = search.restaurant_id.trim();
    if (search.name.trim() && entity !== 'promotions') query.name = search.name.trim();
    if (entity === 'restaurants') query.include_hidden = 'true';
    const result = envelope.parse(
      await http.get<unknown>(`/api/v1/admin/${paths[entity]}`, {
        ...base,
        query,
        ...(signal ? { signal } : {}),
      }),
    );
    const rows = result.list ?? [];
    return {
      rows,
      total: result.meta?.total ?? rows.length,
      pageCount: result.meta?.page_count,
    };
  },
  async detail(entity: StorefrontEntity, id: string): Promise<StorefrontItem> {
    const result = detailEnvelope.parse(
      await http.get<unknown>(
        `/api/v1/admin/${paths[entity]}/${encodeURIComponent(id)}`,
        base,
      ),
    );
    const item = result.data?.[detailKeys[entity]];
    const parsed = storefrontItemSchema.safeParse(item);
    if (parsed.success) {
      if (entity !== 'restaurants' || parsed.data.week_schedule?.length)
        return parsed.data;
      // The per-day week rides beside the restaurant, not inside it.
      const week = z
        .array(storefrontDayScheduleSchema)
        .safeParse(result.list ?? result.data?.week_schedule ?? []);
      return week.success && week.data.length
        ? { ...parsed.data, week_schedule: week.data }
        : parsed.data;
    }
    throw new Error('Catalog detail response has an invalid item shape');
  },
  create(entity: StorefrontEntity, body: Record<string, unknown>) {
    const suffix = entity === 'promotions' ? '/batch' : '';
    return http.post(`/api/v1/admin/${paths[entity]}${suffix}`, body, base);
  },
  update(entity: StorefrontEntity, id: string, body: Record<string, unknown>) {
    return http.patch(
      `/api/v1/admin/${paths[entity]}/${encodeURIComponent(id)}`,
      body,
      base,
    );
  },
  remove(entity: StorefrontEntity, id: string) {
    return http.delete(`/api/v1/admin/${paths[entity]}/${encodeURIComponent(id)}`, base);
  },
  upsertImages(
    entity: 'restaurants' | 'products',
    id: string,
    items: { image_type: 'ICON' | 'BACKGROUND' | 'MAIN' | 'COMMON'; link: string }[],
  ) {
    return http.put(
      `/api/v1/admin/images/${entity === 'restaurants' ? 'restaurant' : 'product'}/${encodeURIComponent(id)}`,
      { items },
      base,
    );
  },
  removeImage(
    entity: 'restaurants' | 'products',
    id: string,
    selector: 'icon' | 'background' | 'main' | 'common',
    imageId?: string,
  ) {
    return http.delete(
      `/api/v1/admin/images/${entity === 'restaurants' ? 'restaurant' : 'product'}/${encodeURIComponent(id)}/${selector}`,
      { ...base, ...(imageId ? { query: { image_id: imageId } } : {}) },
    );
  },
};
